import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "athlon_auth_token";

export function setAuthToken(token) {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* storage disabled */ }
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* noop */ }
  }
}
export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000, // 30s: evita quedar colgado si el ingress no responde
});

// Fallback para iOS Safari / PWA donde ITP puede descartar la cookie cross-site:
// si tenemos un token guardado, lo mandamos como Authorization: Bearer.
api.interceptors.request.use((config) => {
  const t = getAuthToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Retry automático para "Network Error" intermitentes (cortes de ingress,
// reconexión WiFi, container cold-start). No reintenta errores HTTP reales
// (4xx/5xx) — solo cuando no hubo respuesta del servidor.
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 600;

function isRetriableNetworkError(err) {
  // Sin response = nunca llegó al servidor (network, DNS, timeout, CORS fail)
  if (err?.response) return false;
  // No reintentar si la app abortó la request manualmente
  if (axios.isCancel?.(err)) return false;
  return true;
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err?.response?.status === 401) {
      setAuthToken(null);
    }

    const config = err?.config;
    if (config && isRetriableNetworkError(err)) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        const delay = RETRY_DELAY_MS * Math.pow(2, config.__retryCount - 1);
        await new Promise((r) => setTimeout(r, delay));
        return api(config);
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export function formatApiError(err) {
  // Error de red sin respuesta del servidor (timeout, ingress caído, offline)
  if (err && !err.response) {
    if (err.code === "ECONNABORTED") return "La conexión demoró demasiado. Probá de nuevo.";
    return "Error de conexión. Verificá tu internet y volvé a intentar.";
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Error desconocido";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  return String(detail);
}
